import { LanguageProvider } from '@web_opxp/language';
import { useEffect, useRef } from 'react';
import { Provider } from 'react-redux';
import { Route, Routes } from 'react-router';
import { GalleryTargetOrigin, IsDebug, LocalesBaseUrl } from '../base/env';
import AllVideosPage from './AllVideosPage';
import GalleryPage from './GalleryPage';
import { store } from './store';

const GalleryApp = () => {
  const lastSentDimensions = useRef({ width: null as number | null, height: null as number | null });
  const sizeUpdateTimeout = useRef<NodeJS.Timeout | null>(null);

  // Function to send frame size to parent
  const sendFrameSize = () => {
    const width = document.documentElement.scrollWidth || document.body.scrollWidth;
    const height = document.documentElement.scrollHeight || document.body.scrollHeight;

    // Only send message if dimensions have changed
    if (lastSentDimensions.current.width !== width || lastSentDimensions.current.height !== height) {
      const sizeMessage = {
        type: 'FRAME_SIZE',
        width: width,
        height: height,
      };

      window.parent.postMessage(sizeMessage, GalleryTargetOrigin);

      // Update stored dimensions
      lastSentDimensions.current.width = width;
      lastSentDimensions.current.height = height;
    }
  };

  // Initialize size observer and send initial size
  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;

    const throttledSendFrameSize = () => {
      if (sizeUpdateTimeout.current) {
        clearTimeout(sizeUpdateTimeout.current);
      }
      sizeUpdateTimeout.current = setTimeout(sendFrameSize, 100);
    };

    // Initialize ResizeObserver if supported
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        throttledSendFrameSize();
      });

      // Observe changes to the document body and documentElement
      resizeObserver.observe(document.body);
      resizeObserver.observe(document.documentElement);
    }

    // Initialize MutationObserver to watch for DOM changes
    mutationObserver = new MutationObserver(() => {
      throttledSendFrameSize();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    // Listen for window resize events
    const handleResize = () => {
      throttledSendFrameSize();
    };

    window.addEventListener('resize', handleResize);

    // Send initial size after a short delay to ensure content is rendered
    setTimeout(sendFrameSize, 100);

    // Cleanup function
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
      window.removeEventListener('resize', handleResize);
      if (sizeUpdateTimeout.current) {
        clearTimeout(sizeUpdateTimeout.current);
      }
    };
  }, []);

  return (
    <Provider store={store}>
      <LanguageProvider
        fallbackNS="prereg"
        localesPath={LocalesBaseUrl}
        debug={IsDebug}
        supportedLngs={['de']}
        ns={['prereg']}
        defaultNS="prereg"
      >
        <Routes>
          <Route path="" element={<GalleryPage />} />
          <Route path="all" element={<AllVideosPage />} />
        </Routes>
      </LanguageProvider>
    </Provider>
  );
};

export default GalleryApp;
